import { type Flags, useFlags } from 'src/feature-flags/FlagsHook';

// Extension → MIME type(s) mapping. Single source of truth for allowed data upload file types.
const ALLOWED_FILE_TYPES: Record<string, string[]> = {
  '.png': ['image/png'],
  '.jpg': ['image/jpg'],
  '.jpeg': ['image/jpeg'],
  '.pdf': ['application/pdf'],
  '.doc': ['application/msword'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.txt': ['text/plain'],
  '.md': ['text/markdown'],
  '.csv': ['text/csv'],
  '.xls': ['application/vnd.ms-excel'],
  '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  '.xlsm': ['application/vnd.ms-excel.sheet.macroEnabled.12'],
  '.mp3': ['audio/mpeg'],
  '.mp4': ['video/mp4'],
  '.wav': ['audio/wav'],
  '.aac': ['audio/aac'],
  '.flac': ['audio/flac'],
  '.ogg': ['audio/ogg'],
  '.m4a': ['audio/mp4', 'audio/x-m4a'],
  '.mov': ['video/quicktime'],
  '.eml': ['message/rfc822'],
};

// Extensions that are only accepted while their flag is on.
const EXTENSION_FLAGS: Record<string, keyof Flags> = {
  '.md': 'flagEnabledMarkdownSupport',
};

type AllowedFileTypes = {
  extensions: string[];
  fileButtonAccept: string;
  dropzoneAccept: Record<string, string[]>;
  formatsLabel: string;
};

function buildAllowedFileTypes(extensions: string[]): AllowedFileTypes {
  const dropzoneAccept: Record<string, string[]> = {};
  for (const extension of extensions) {
    for (const mimeType of ALLOWED_FILE_TYPES[extension]) {
      dropzoneAccept[mimeType] = [...(dropzoneAccept[mimeType] ?? []), extension];
    }
  }

  return {
    extensions,
    fileButtonAccept: extensions.join(','),
    dropzoneAccept,
    formatsLabel: extensions.map(extension => extension.slice(1).toUpperCase()).join(', '),
  };
}

const EVERY_EXTENSION = Object.keys(ALLOWED_FILE_TYPES);

export function useAllowedFileTypes(): AllowedFileTypes {
  const flags = useFlags();
  return buildAllowedFileTypes(
    EVERY_EXTENSION.filter(extension => {
      const flag = EXTENSION_FLAGS[extension];
      return flag === undefined || flags[flag] === true;
    }),
  );
}

// Surfaces that keep their own document list pass it through here. Only the
// flag-gated extensions are removed.
export function useSupportedExtensions(extensions: string[]): string[] {
  const { extensions: allowed } = useAllowedFileTypes();
  return extensions.filter(
    extension => !(extension in EXTENSION_FLAGS) || allowed.includes(extension),
  );
}

export const MAX_UPLOAD_SIZE_BYTES = 2 * 1024 ** 3;
const MAX_UPLOAD_SIZE_LABEL = '2 GB';

type UploadRejectionReason = 'type' | 'size';

export type UploadRejection = {
  filename: string;
  reason: UploadRejectionReason;
};

// Browsers disagree on the MIME type of formats like .csv, so the extension decides, as the
// backend does. Mantine's accept only narrows the file picker and the drag-hover hint, which is
// why a dropped file is re-checked here. Same reasoning as chats/chatFileTypes.ts.
function uploadFileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
}

export function uploadRejectionReason(
  file: File,
  extensions: string[],
): UploadRejectionReason | null {
  if (!extensions.includes(uploadFileExtension(file.name))) return 'type';
  if (file.size > MAX_UPLOAD_SIZE_BYTES) return 'size';
  return null;
}

// One notification for a whole batch, grouped by reason, so dropping several rejected files at
// once does not stack a separate toast per file.
export function uploadRejectionsMessage(
  rejections: UploadRejection[],
  formatsLabel: string,
): string {
  const wrongType = rejections.filter(rejection => rejection.reason === 'type');
  const tooLarge = rejections.filter(rejection => rejection.reason === 'size');
  const parts: string[] = [];
  if (wrongType.length) {
    parts.push(
      `Unsupported type: ${filenamesLabel(wrongType)}. Supported formats: ${formatsLabel}.`,
    );
  }
  if (tooLarge.length) {
    parts.push(`Over ${MAX_UPLOAD_SIZE_LABEL}: ${filenamesLabel(tooLarge)}.`);
  }
  return parts.join(' ');
}

export function uploadRejectionsTitle(rejections: UploadRejection[]): string {
  return rejections.length === 1
    ? `Could not upload ${rejections[0].filename}`
    : `Could not upload ${rejections.length} files`;
}

function filenamesLabel(rejections: UploadRejection[]): string {
  return rejections.map(rejection => rejection.filename).join(', ');
}

export const isAudioFile = (fileType: string, fileName: string): boolean =>
  fileType?.startsWith('audio/') || /\.(mp3|wav|aac|flac|ogg|m4a)$/i.test(fileName || '');

// Stored MIME types for recordings vary by source (browser uploads, the mobile app, the
// meeting bot) and app version, so match on the audio/ prefix or the file extension rather
// than an exact MIME list. video/mp4 and .mp4/.webm cover bot-recorded meetings.
export const isRecordingFile = (fileType: string, fileName: string): boolean =>
  fileType?.startsWith('audio/') ||
  fileType === 'video/mp4' ||
  /\.(mp3|wav|aac|flac|ogg|m4a|mp4|webm)$/i.test(fileName || '');
